import ExcelJS from 'exceljs';
import {
	formatDceRowApproversForExport,
	formatDceRowOwnersForExport
} from './dceQuestionPermissionRows';

/**
 * Builds an .xlsx workbook buffer with wrapped text, column widths, and light grid styling
 * for the DCE question permissions export.
 *
 * @param {object[]} rows - Same shape as {@link buildDceQuestionPermissionRows} output
 * @param {string[]} headerRow - Five column titles
 * @param {string} sheetName - Max 31 chars (Excel limit)
 * @param {(level: number) => string} [tApproverLevel] - Localized level label (e.g. "Level 1")
 * @returns {Promise<ArrayBuffer>}
 */
export async function buildDceQuestionPermissionsExcelBuffer(
	rows,
	headerRow,
	sheetName,
	tApproverLevel
) {
	const safeName = (sheetName || 'Sheet1').slice(0, 31);
	const workbook = new ExcelJS.Workbook();
	const ws = workbook.addWorksheet(safeName, {
		views: [{ state: 'frozen', ySplit: 1 }]
	});

	ws.addRow(headerRow);
	const headerRowExcel = ws.getRow(1);
	headerRowExcel.font = { bold: true, color: { argb: 'FF1E293B' }, size: 11 };
	headerRowExcel.fill = {
		type: 'pattern',
		pattern: 'solid',
		fgColor: { argb: 'FFE8EEF4' }
	};
	headerRowExcel.alignment = { vertical: 'middle', wrapText: true };
	headerRowExcel.height = 22;

	const thinBorder = {
		style: 'thin',
		color: { argb: 'FFD1D5DB' }
	};

	for (const row of rows || []) {
		ws.addRow([
			row.sectionTitle || '',
			(row.subSectionTitles || []).join(' › '),
			row.questionName || '',
			formatDceRowOwnersForExport(row),
			formatDceRowApproversForExport(row, tApproverLevel)
		]);
	}

	ws.columns = [
		{ width: 28 },
		{ width: 32 },
		{ width: 48 },
		{ width: 42 },
		{ width: 46 }
	];

	ws.eachRow((row, rowNumber) => {
		row.eachCell((cell) => {
			cell.alignment = { vertical: 'top', wrapText: true };
			cell.border = {
				top: thinBorder,
				left: thinBorder,
				bottom: thinBorder,
				right: thinBorder
			};
		});
		if (rowNumber > 1) {
			row.height = undefined;
		}
	});

	return workbook.xlsx.writeBuffer();
}
